/**
 * Mailbag collections: shared vocabulary. Deliberately dependency-free (its
 * only import is the equally pure mentionText helper) so models, server code,
 * and client bundles can all import it.
 */

import { buildMentionAttrs, getMentionDisplayText } from "./mentionText.ts";

/**
 * null  = a normal collection
 * open  = taking questions (the ask form shows)
 * closed = no longer taking questions; answers stay and the collection looks
 *          like a normal one again
 */
export type MailbagStatus = "open" | "closed";
export const MAILBAG_STATUSES: readonly MailbagStatus[] = ["open", "closed"];

/** Who may ask. Mirrors rec_leagues.comment_permissions. */
export type MailbagAskPermissions = "everyone" | "members";
export const MAILBAG_ASK_PERMISSIONS: readonly MailbagAskPermissions[] = [
  "everyone",
  "members",
];

/**
 * awaiting_membership - asked on a members-only mailbag by a non-member; kept
 *                       out of the curator's queue until the asker joins
 * open                - in the curator's queue
 * answered            - at least one answer rec has been published
 * archived            - dismissed by the curator
 */
export type MailbagQuestionStatus =
  | "awaiting_membership"
  | "open"
  | "answered"
  | "archived";
export const MAILBAG_QUESTION_STATUSES: readonly MailbagQuestionStatus[] = [
  "awaiting_membership",
  "open",
  "answered",
  "archived",
];

/** The curator-facing queue tabs; awaiting_membership stays parked/hidden. */
export const MAILBAG_QUEUE_STATUSES = ["open", "answered", "archived"] as const;
export type MailbagQueueStatus = (typeof MAILBAG_QUEUE_STATUSES)[number];
export const MAILBAG_QUEUE_DEFAULT_LIMIT = 100;
export const MAILBAG_QUEUE_MAX_LIMIT = 200;

export type MailbagQuestionSource = "web" | "app";

export const MAILBAG_QUESTION_MAX_LENGTH = 1000;
export const MAILBAG_ASKER_NAME_MAX_LENGTH = 100;

/**
 * What the curator sees in place of an anonymous asker's name when they
 * didn't pick a pseudonym.
 */
export const MAILBAG_ANONYMOUS_NAME = "Anonymous";

// ---- Custom form fields ----

/**
 * A curator-defined extra field on the ask form. `id` is stable across edits
 * (assigned once, server-side when missing) so responses can be matched to
 * fields; `label` is what askers see; `required` is enforced on submit.
 */
export type MailbagFormFieldType = "string" | "number" | "boolean";
export const MAILBAG_FORM_FIELD_TYPES: readonly MailbagFormFieldType[] = [
  "string",
  "number",
  "boolean",
];

export interface MailbagFormField {
  id: string;
  label: string;
  type: MailbagFormFieldType;
  required: boolean;
}

export const MAILBAG_FORM_MAX_FIELDS = 5;
export const MAILBAG_FORM_FIELD_LABEL_MAX_LENGTH = 80;
export const MAILBAG_FORM_FIELD_ID_MAX_LENGTH = 40;
export const MAILBAG_FORM_STRING_ANSWER_MAX_LENGTH = 500;

/** An asker's answer, keyed by field id. Unanswered optional fields are absent or null. */
export type MailbagFormAnswerValue = string | number | boolean | null;
export type MailbagFormAnswers = Record<string, MailbagFormAnswerValue>;

/**
 * What gets stored on the question: each field as it was at submit time
 * (label and type included, so the curator's view survives later edits to
 * the form) with the asker's answer. `value` is null for an unanswered
 * optional field.
 */
export interface MailbagFormResponse {
  id: string;
  label: string;
  type: MailbagFormFieldType;
  value: MailbagFormAnswerValue;
}

/** The columns a collection carries; shared by every serializer. */
export interface MailbagCollectionSettings {
  mailbag_status: MailbagStatus | null;
  mailbag_ask_permissions: MailbagAskPermissions;
  mailbag_answers_members_only: boolean;
  mailbag_form_fields: MailbagFormField[];
}

/** A settings change: any subset of the columns (status may be null). */
export type MailbagSettingsPatch = Partial<
  Omit<MailbagCollectionSettings, "mailbag_status"> & {
    mailbag_status: MailbagStatus | null;
  }
>;

export const EMPTY_MAILBAG_SETTINGS: MailbagCollectionSettings = {
  mailbag_status: null,
  mailbag_ask_permissions: "everyone",
  mailbag_answers_members_only: false,
  mailbag_form_fields: [],
};

/**
 * Reads the mailbag settings off a collection-shaped object, defaulting each
 * column: client payloads from before a column existed omit it.
 */
export const mailbagSettingsFromCollection = (
  collection: Partial<MailbagCollectionSettings> | null | undefined,
): MailbagCollectionSettings => ({
  mailbag_status: collection?.mailbag_status ?? null,
  mailbag_ask_permissions: collection?.mailbag_ask_permissions ?? "everyone",
  mailbag_answers_members_only:
    collection?.mailbag_answers_members_only ?? false,
  mailbag_form_fields: Array.isArray(collection?.mailbag_form_fields)
    ? collection.mailbag_form_fields
    : [],
});

const generateFormFieldId = (): string =>
  `f_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/**
 * A fresh, unsaved field for the settings builder. The id is minted here so
 * React keys and edits are stable before saving; the server keeps any id it
 * is given, so it simply becomes the field's id.
 */
export const newMailbagFormField = (): MailbagFormField => ({
  id: generateFormFieldId(),
  label: "",
  type: "string",
  required: false,
});

/**
 * Validates and normalizes a curator's form-field definitions (from the
 * settings UI or an API client): trims labels, drops unknown keys, assigns
 * ids to new fields, and rejects anything malformed with a message the
 * curator can act on. Dependency-free so the settings forms can run the
 * same check before saving.
 */
export type MailbagFormFieldsResult =
  | { ok: true; fields: MailbagFormField[] }
  | { ok: false; error: string };

export const normalizeMailbagFormFields = (
  input: unknown,
): MailbagFormFieldsResult => {
  if (input == null) return { ok: true, fields: [] };
  if (!Array.isArray(input)) {
    return { ok: false, error: "Form fields must be a list." };
  }
  if (input.length > MAILBAG_FORM_MAX_FIELDS) {
    return {
      ok: false,
      error: `You can add up to ${MAILBAG_FORM_MAX_FIELDS} fields.`,
    };
  }
  const fields: MailbagFormField[] = [];
  const seenIds = new Set<string>();
  for (const raw of input) {
    if (!raw || typeof raw !== "object") {
      return { ok: false, error: "Each form field needs a label and a type." };
    }
    const candidate = raw as Record<string, unknown>;
    const label =
      typeof candidate.label === "string"
        ? candidate.label.trim().replace(/\s+/g, " ")
        : "";
    if (!label) {
      return { ok: false, error: "Every form field needs a label." };
    }
    if (label.length > MAILBAG_FORM_FIELD_LABEL_MAX_LENGTH) {
      return {
        ok: false,
        error: `Field labels can be up to ${MAILBAG_FORM_FIELD_LABEL_MAX_LENGTH} characters.`,
      };
    }
    const type = candidate.type;
    if (
      typeof type !== "string" ||
      !(MAILBAG_FORM_FIELD_TYPES as readonly string[]).includes(type)
    ) {
      return { ok: false, error: `"${label}" needs a field type.` };
    }
    const rawId = typeof candidate.id === "string" ? candidate.id.trim() : "";
    if (rawId.length > MAILBAG_FORM_FIELD_ID_MAX_LENGTH) {
      return { ok: false, error: "Form field ids are malformed." };
    }
    let id = rawId || generateFormFieldId();
    // A client that copied a field (or a retry) can send the same id twice;
    // keep the first and mint the rest a fresh id rather than failing.
    while (seenIds.has(id)) id = generateFormFieldId();
    seenIds.add(id);
    fields.push({
      id,
      label,
      type: type as MailbagFormFieldType,
      required: candidate.required === true,
    });
  }
  return { ok: true, fields };
};

/**
 * Validates an asker's answers against the form and builds the snapshot to
 * store. Enforces required fields and types; coerces numbers typed as
 * strings (an HTML input's value); ignores answers to fields the form
 * doesn't have. `error` names the first problem, in asker-facing copy.
 * Mirrored in mobile/lib/util/mailbagFormFields.ts — keep in sync.
 */
export type MailbagFormResponsesResult =
  | { ok: true; responses: MailbagFormResponse[] | null }
  | { ok: false; error: string; fieldId: string | null };

export const validateMailbagFormResponses = (
  fields: MailbagFormField[],
  answers: unknown,
): MailbagFormResponsesResult => {
  if (fields.length === 0) return { ok: true, responses: null };
  const provided: Record<string, unknown> =
    answers && typeof answers === "object" && !Array.isArray(answers)
      ? (answers as Record<string, unknown>)
      : {};
  const responses: MailbagFormResponse[] = [];
  for (const field of fields) {
    const raw = provided[field.id];
    const missing =
      raw === undefined ||
      raw === null ||
      (typeof raw === "string" && raw.trim() === "");
    if (missing) {
      if (field.required) {
        return {
          ok: false,
          error: `"${field.label}" is required.`,
          fieldId: field.id,
        };
      }
      responses.push({
        id: field.id,
        label: field.label,
        type: field.type,
        value: null,
      });
      continue;
    }
    let value: MailbagFormAnswerValue;
    if (field.type === "string") {
      if (typeof raw !== "string") {
        return {
          ok: false,
          error: `"${field.label}" needs a text answer.`,
          fieldId: field.id,
        };
      }
      value = raw.trim();
      if (value.length > MAILBAG_FORM_STRING_ANSWER_MAX_LENGTH) {
        return {
          ok: false,
          error: `"${field.label}" can be up to ${MAILBAG_FORM_STRING_ANSWER_MAX_LENGTH} characters.`,
          fieldId: field.id,
        };
      }
    } else if (field.type === "number") {
      const parsed =
        typeof raw === "number"
          ? raw
          : typeof raw === "string"
            ? Number(raw.trim())
            : NaN;
      if (!Number.isFinite(parsed)) {
        return {
          ok: false,
          error: `"${field.label}" needs a number.`,
          fieldId: field.id,
        };
      }
      value = parsed;
    } else {
      if (typeof raw !== "boolean") {
        return {
          ok: false,
          error: `"${field.label}" needs a yes or no.`,
          fieldId: field.id,
        };
      }
      value = raw;
    }
    responses.push({
      id: field.id,
      label: field.label,
      type: field.type,
      value,
    });
  }
  return { ok: true, responses };
};

/** How a stored answer reads back to the curator. */
export const formatMailbagFormAnswer = (
  response: Pick<MailbagFormResponse, "type" | "value">,
): string => {
  if (response.value === null || response.value === undefined) return "—";
  if (response.type === "boolean") return response.value ? "Yes" : "No";
  return String(response.value);
};

/**
 * Members-only questions get members-only answers by default: when a settings
 * change moves "who can ask" to members and says nothing about answers, turn
 * the answers default on (where the league can paywall recs at all). The
 * curator can still switch it off afterwards, and change it on each rec —
 * this is a default, not a rule. Shared by the settings form and the
 * collection routes so API clients and the dashboard agree.
 */
export const withMailbagAnswersDefault = <T extends MailbagSettingsPatch>(
  current: Pick<MailbagCollectionSettings, "mailbag_ask_permissions"> | null,
  patch: T,
  canPaywallRecs: boolean,
): T => {
  const becomingMembersOnly =
    patch.mailbag_ask_permissions === "members" &&
    (current?.mailbag_ask_permissions ?? "everyone") !== "members";
  if (
    !becomingMembersOnly ||
    !canPaywallRecs ||
    patch.mailbag_answers_members_only !== undefined
  ) {
    return patch;
  }
  return { ...patch, mailbag_answers_members_only: true };
};

export const isMailbagOpen = (
  collection: Pick<MailbagCollectionSettings, "mailbag_status"> | null | undefined,
): boolean => collection?.mailbag_status === "open";

export const isMailbag = (
  collection: Pick<MailbagCollectionSettings, "mailbag_status"> | null | undefined,
): boolean => !!collection?.mailbag_status;

/**
 * The default first paragraph of an answer rec's description: the question,
 * quoted and italicized, credited to the asker, followed by an empty paragraph
 * for the curator's own words. A TipTap doc (the composer's native format).
 * Italic rather than blockquote because every rich-text renderer (web static,
 * email, journal, RSS, and the app) already handles italic marks and none
 * handles blockquotes. Fully editable once in the composer.
 */
export interface MailbagAskerRecLeague {
  id: number;
  name: string;
  subdomain: string;
}

export const buildMailbagAnswerPrefixDoc = ({
  question,
  askerName,
  askerRecLeague = null,
}: {
  question: string;
  askerName: string;
  // The asker's own league, when they have one: credited as an @mention
  // (a link to them) instead of their name as plain text.
  askerRecLeague?: MailbagAskerRecLeague | null;
}) => {
  const trimmedQuestion = question.trim().replace(/\s+/g, " ");
  const trimmedName = askerName.trim();
  const italic = [{ type: "italic" }];
  const content = askerRecLeague
    ? [
        { type: "text", marks: italic, text: `“${trimmedQuestion}” - ` },
        { type: "mention", attrs: buildMentionAttrs(askerRecLeague) },
      ]
    : [
        {
          type: "text",
          marks: italic,
          text: trimmedName
            ? `“${trimmedQuestion}” - ${trimmedName}`
            : `“${trimmedQuestion}”`,
        },
      ];
  return {
    type: "doc",
    content: [{ type: "paragraph", content }, { type: "paragraph" }],
  };
};

/**
 * The plain text of a TipTap doc: text nodes plus @mention handles, one line
 * per paragraph. Used to tell an untouched answer seed apart from one the
 * curator has typed into, without depending on object identity (editors echo
 * a fresh doc object on mount and on every edit).
 */
export const getDocPlainText = (doc: unknown): string => {
  const lines: string[] = [];
  const walk = (node: any, line: string[]) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "text" && typeof node.text === "string") {
      line.push(node.text);
    } else if (node.type === "mention") {
      line.push(getMentionDisplayText(node.attrs));
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) walk(child, line);
    }
  };
  const root: any = doc;
  if (root && Array.isArray(root.content)) {
    for (const block of root.content) {
      const line: string[] = [];
      walk(block, line);
      lines.push(line.join(""));
    }
  }
  return lines.join("\n").trim();
};

export const isMailbagSeedUntouched = (doc: unknown, seed: unknown): boolean =>
  seed != null &&
  (doc === seed || (doc != null && getDocPlainText(doc) === getDocPlainText(seed)));

/**
 * What a collection surface knows about the viewer's relationship to its
 * mailbag. Served by GET /api/mailbag/collections/:id/viewer and by the web
 * collection page loader; consumed by the ask form.
 */
export interface MailbagViewerInfo {
  mailbag: MailbagCollectionSettings;
  /** The viewer's own latest question here, if any. */
  viewerState: "awaiting_membership" | "submitted" | null;
  canAsk: boolean;
  askBlockedReason: "closed" | "blocked" | "banned" | null;
  /** Submitting will park the question until the viewer joins. */
  membershipRequired: boolean;
}
